import StorageManager from './StorageManager'
import { translateManager as t } from './TranslateManager'
import { analytics } from './Analytics/Analytics'
import fromPairs from 'lodash/fromPairs'
import {
  type CanMakePaymentResult,
  type PaymentMethod,
  type PaymentRequest,
  type Stripe,
} from '@stripe/stripe-js'
import { mixtilesAxios } from '../utils/ApiUtils'
import { countryManager } from './CountryManager'
import { logger } from './logger'
import { getOrderLocalId } from './OrderUtils'
import { PaymentMethodType } from './PaymentManager.types'
import {
  ADDITIONAL_PAYMENT_INFO_KEYS,
  DEFAULT_PAYPAL_ACCOUNT_NAME,
  DEFAULT_STRIPE_ACCOUNT_NAME,
  KEY_PAYMENT_CURRENT_CARD,
  KEY_PAYMENT_METHOD_ID,
  KEY_PAYMENT_METHOD_TYPE,
  KEY_PAYPAL_ACCOUNT_NAME,
  KEY_STRIPE_ACCOUNT_NAME,
  KEY_STRIPE_CUSTOMER,
  OLD_PAYPAL_ACCOUNT_NAME,
  PAYPAL_ACCOUNTS_DATA,
  STRIPE_ACCOUNTS_DATA,
  STRIPE_ERROR_REASON_CUSTOMER_MISSING,
} from '../stores/paymentStore/payment.consts'
import { isReusablePaymentMethod } from '../stores/paymentStore/payment.utils'
import { type StripeAccountName } from '../stores/paymentStore/payment.types'

type StripeCustomer = { id: string }

class PaymentManager {
  static payPalTransactionId: string | null | undefined

  static async createPaymentIntent(
    orderPriceSummary: any,
    isExpressCheckout = false
  ) {
    // this method is called when 'place order' is clicked and before the
    // photos finished to upload, it will just get a funds hold confirmation
    // and return a paymentIntentId which we can use later to make the
    // actual charge
    const paymentMethodId = isExpressCheckout
      ? undefined
      : this.getStripePaymentMethodId()
    const customerId = isExpressCheckout
      ? undefined
      : this.getStripeCustomer()?.id
    logger.info(`PaymentManager.createPaymentIntent paymentMethodId: ${paymentMethodId}
                          customerId: ${customerId}
                          stripeAccountName: ${this.getStripeAccountName()}`)

    return mixtilesAxios.post('v4/stripe/createPaymentIntent', {
      paymentMethodId,
      customerId,
      stripeAccountName: this.getStripeAccountName(),
      totalCost: orderPriceSummary.totalPrice,
      currency: orderPriceSummary.currency,
      localId: getOrderLocalId(),
    })
  }

  static canMakeAppleOrGooglePay({ stripe }: { stripe: Stripe }) {
    // initialize payment request with default parameters. This way we know if the client
    // has a configured payment method that can be used with PaymentRequest API.
    const stripeAccount = this.getStripeAccount()
    const paymentRequest = stripe.paymentRequest({
      country: stripeAccount.country,
      currency: stripeAccount.bankCurrency,
      total: {
        label: t.get('order.checkout.payment_summary_rows.total'),
        amount: 0,
      },
      displayItems: [],
      requestPayerEmail: true,
      requestPayerName: true,
      // TODO: add request shipping and convert address to our format.
      // requestShipping: true,
    })

    return paymentRequest.canMakePayment().then((result) => {
      return {
        ...this._canMakeApplePay(paymentRequest, result),
        ...this._canMakeGooglePay(paymentRequest, result),
      }
    })
  }

  static _canMakeApplePay(
    paymentRequest: PaymentRequest,
    stripePaymentResult: CanMakePaymentResult | null
  ) {
    if (stripePaymentResult?.applePay) {
      analytics.setUserProperties({ 'Has Apple Pay': true })
      if (!PaymentManager.getSelectedPaymentMethodType()) {
        analytics.track('Apple Pay Set By Default')
        PaymentManager.selectApplePay()
      }
      return {
        canMakePayment: true,
        paymentRequest,
        paymentType: PaymentMethodType.APPLE_PAY,
        canMakeApplePay: true,
      }
    }
    return { canMakeApplePay: false }
  }

  static _canMakeGooglePay(
    paymentRequest: PaymentRequest,
    stripePaymentResult: CanMakePaymentResult | null
  ) {
    if (stripePaymentResult?.googlePay) {
      analytics.setUserProperties({ 'Has Google Pay': true })
      if (!PaymentManager.getSelectedPaymentMethodType()) {
        analytics.track('Google Pay Set By Default')
        PaymentManager.selectGooglePay()
      }
      return {
        canMakePayment: true,
        paymentRequest,
        paymentType: PaymentMethodType.GOOGLE_PAY,
        canMakeGooglePay: true,
      }
    }
    return { canMakeGooglePay: false }
  }

  static async addStripePaymentMethod(
    stripePaymentMethod: Pick<PaymentMethod, 'id' | 'card'>,
    email: string | undefined,
    paymentMethodType = PaymentMethodType.CREDIT_CARD
  ) {
    if (isReusablePaymentMethod(paymentMethodType)) {
      await this.savePaymentMethodToCustomer(stripePaymentMethod, email)
    }

    this.savePaymentDataLocally(paymentMethodType, stripePaymentMethod)

    return true
  }

  static async savePaymentMethodToCustomer(
    stripePaymentMethod: Pick<PaymentMethod, 'id'>,
    email: string | undefined
  ) {
    // getting stripe customer from local storage
    let stripeCustomer = PaymentManager.getStripeCustomer()
    let response!: Awaited<ReturnType<typeof mixtilesAxios.post>>
    if (stripeCustomer?.id == null) {
      // if there is no stripe customer, we then create a new one
      const data = {
        paymentMethodId: stripePaymentMethod.id,
        email,
        stripeAccountName: this.getStripeAccountName(),
      }
      logger.info('Creating new stripe customer', data)
      response = await mixtilesAxios.post('v3/stripe/createCustomer', data)
    } else if (stripePaymentMethod.id !== this.getStripePaymentMethodId()) {
      // if stripe customer exists, we attach this new
      // payment method to it.
      const data = {
        paymentMethodId: stripePaymentMethod.id,
        customerId: stripeCustomer.id,
        stripeAccountName: this.getStripeAccountName(),
        email,
      }
      response = await mixtilesAxios.post('v3/stripe/attachPaymentMethod', data)

      if (
        response.data.error?.reason === STRIPE_ERROR_REASON_CUSTOMER_MISSING
      ) {
        // A missing customer is not recoverable, so create a new one.
        logger.warning(
          'Stored stripe customer is missing, creating a new one',
          {
            customerId: stripeCustomer.id,
          }
        )
        StorageManager.remove(KEY_STRIPE_CUSTOMER)
        response = await mixtilesAxios.post('v3/stripe/createCustomer', {
          paymentMethodId: stripePaymentMethod.id,
          email,
          stripeAccountName: this.getStripeAccountName(),
        })
      }
    }
    if (response.data && response.data.success) {
      stripeCustomer = response.data.success
    } else if (response.data.error) {
      throw response.data.error
    }

    // save the customer object to local storage
    StorageManager.set(KEY_STRIPE_CUSTOMER, stripeCustomer)
  }

  static savePaymentDataLocally(
    paymentMethodType: PaymentMethodType,
    paymentData: Pick<PaymentMethod, 'id' | 'card'>
  ) {
    // set the payment method
    StorageManager.set(KEY_PAYMENT_METHOD_TYPE, paymentMethodType)

    // save the payment method id to the local storage. This will be required later to make payments.
    StorageManager.set(KEY_PAYMENT_METHOD_ID, paymentData.id)

    if (paymentMethodType === PaymentMethodType.CREDIT_CARD) {
      // save the last card added by the user to the local storage
      StorageManager.set(KEY_PAYMENT_CURRENT_CARD, paymentData.card)
    } else {
      // Remove the card data from local storage because we have overridden KEY_PAYMENT_METHOD_ID
      StorageManager.remove(KEY_PAYMENT_CURRENT_CARD)
    }
  }

  static selectPayPal() {
    this.setPaymentMethodType(PaymentMethodType.PAYPAL)
  }

  static selectApplePay() {
    this.setPaymentMethodType(PaymentMethodType.APPLE_PAY)
  }

  static selectGooglePay() {
    this.setPaymentMethodType(PaymentMethodType.GOOGLE_PAY)
  }

  static setPaymentMethodType(type: PaymentMethodType) {
    StorageManager.set(KEY_PAYMENT_METHOD_TYPE, type)
    analytics.track('Checkout - Payment Method Added', { paymentMethod: type })
  }

  static getPayPalAccountName() {
    // Get cached paypal account if exists. Otherwise, will choose paypal account by user's country.
    let paypalAccountName = StorageManager.get<string>(KEY_PAYPAL_ACCOUNT_NAME)
    if (paypalAccountName && paypalAccountName !== OLD_PAYPAL_ACCOUNT_NAME) {
      return paypalAccountName
    }

    // Choose PayPal account based on the pricing currency, in order to avoid high exchange rates in PayPal.
    paypalAccountName = DEFAULT_PAYPAL_ACCOUNT_NAME

    const userCountry = countryManager.getPricingCountry()
    for (const accountName of Object.keys(PAYPAL_ACCOUNTS_DATA) as Array<
      keyof typeof PAYPAL_ACCOUNTS_DATA
    >) {
      if (
        PAYPAL_ACCOUNTS_DATA[accountName].userCountries.includes(
          userCountry.toUpperCase()
        )
      ) {
        paypalAccountName = accountName
        break
      }
    }

    this.setPayPalAccountName(paypalAccountName)
    return paypalAccountName
  }

  static setPayPalAccountName(paypalAccountName: string) {
    StorageManager.set(KEY_PAYPAL_ACCOUNT_NAME, paypalAccountName)
  }

  static getPayPalTransactionId() {
    return this.payPalTransactionId
  }

  static setPayPalTransactionId(payPalTransactionId: string | null) {
    this.payPalTransactionId = payPalTransactionId
  }

  static clearData() {
    StorageManager.remove(KEY_STRIPE_CUSTOMER)
    StorageManager.remove(KEY_PAYMENT_CURRENT_CARD)
    this.clearPaymentMethod()
    StorageManager.remove(KEY_STRIPE_ACCOUNT_NAME)
    StorageManager.remove(KEY_PAYPAL_ACCOUNT_NAME)
    this.setPayPalTransactionId(null)
  }

  static clearPaymentMethod() {
    StorageManager.remove(KEY_PAYMENT_METHOD_TYPE)
    StorageManager.remove(KEY_PAYMENT_METHOD_ID)
  }

  static getStripeKey() {
    const stripeAccountName = this.getStripeAccountName()
    switch (stripeAccountName) {
      case 'US':
        return window.KEYS.stripeUSKey
      case 'NL':
        return window.KEYS.stripeNLKey
      default:
        logger.warning('Missing stripe key for given stripe account name', {
          stripeAccountName,
        })
        return window.KEYS.stripeUSKey
    }
  }

  static getStripeCardOnlyConfiguration() {
    const stripeAccountName = this.getStripeAccountName()
    switch (stripeAccountName) {
      case 'US':
        return window.KEYS.stripeUSCardOnlyConfiguration
      case 'NL':
        return window.KEYS.stripeEUCardOnlyConfiguration
      default:
        logger.warning('Missing stripe key for given stripe account name', {
          stripeAccountName,
        })
        return null
    }
  }

  static getStripeAccountName() {
    // Get cached stripe account if exists. Otherwise, will choose stripe account by user's country.
    let stripeAccountName = StorageManager.get<StripeAccountName>(
      KEY_STRIPE_ACCOUNT_NAME
    )
    if (stripeAccountName) {
      return stripeAccountName
    }

    if (this.getStripeCustomer()) {
      // Stripe customer exists in a specific stripe account, so if stripe customer is already created, but
      // no stripe account name is cached in the local storage, should use old default stripe account.
      this.setStripeAccountName(DEFAULT_STRIPE_ACCOUNT_NAME)
      return DEFAULT_STRIPE_ACCOUNT_NAME
    }

    // Choose Stripe account based on the pricing currency, in order to avoid high exchange rates in Stripe.
    stripeAccountName = DEFAULT_STRIPE_ACCOUNT_NAME
    const userCountry = countryManager.getPricingCountry()
    for (const accountName of Object.keys(
      STRIPE_ACCOUNTS_DATA
    ) as StripeAccountName[]) {
      if (
        STRIPE_ACCOUNTS_DATA[accountName].userCountries.includes(
          userCountry.toUpperCase()
        )
      ) {
        stripeAccountName = accountName
        break
      }
    }

    this.setStripeAccountName(stripeAccountName)
    return stripeAccountName
  }

  static getStripeAccount() {
    return STRIPE_ACCOUNTS_DATA[this.getStripeAccountName()]
  }

  static setStripeAccountName(stripeAccountName: StripeAccountName) {
    StorageManager.set(KEY_STRIPE_ACCOUNT_NAME, stripeAccountName)
  }

  static getStripeCustomer() {
    return StorageManager.get<StripeCustomer>(KEY_STRIPE_CUSTOMER)
  }

  static getStripePaymentMethodId() {
    return StorageManager.get<string>(KEY_PAYMENT_METHOD_ID)
  }

  static getSelectedPaymentMethodType() {
    const selectedPaymentMethod = StorageManager.get<PaymentMethodType>(
      KEY_PAYMENT_METHOD_TYPE
    )
    // Solves an issue when CREDIT_CARD was set, but no credit card was added (user closed the dialog without adding one)
    if (
      selectedPaymentMethod === PaymentMethodType.CREDIT_CARD &&
      !this.getStripePaymentMethodId()
    ) {
      return null
    }
    return selectedPaymentMethod
  }

  static getPaymentMethodForOrder(payPalTransactionId: string | null = null) {
    const selectedPaymentMethodType =
      PaymentManager.getSelectedPaymentMethodType()
    const paymentMethodId = this.getStripePaymentMethodId()
    switch (selectedPaymentMethodType) {
      case PaymentMethodType.CREDIT_CARD:
        return {
          type: selectedPaymentMethodType,
          ...PaymentManager.getSelectedCard(),
        }
      case PaymentMethodType.PAYPAL:
        return {
          type: PaymentMethodType.PAYPAL,
          transactionId:
            payPalTransactionId || PaymentManager.getPayPalTransactionId(),
        }
      case undefined:
      case null:
        logger.warning('Received selected payment method without a type.', {
          paymentMethodId,
        })
        return null
      default:
        return { type: selectedPaymentMethodType, paymentMethodId }
    }
  }

  static getSelectedCard() {
    const stripeCustomer = PaymentManager.getStripeCustomer()
    const currentCard = StorageManager.get<PaymentMethod.Card>(
      KEY_PAYMENT_CURRENT_CARD
    )
    if (stripeCustomer == null || !currentCard) {
      return null
    }
    return currentCard
  }

  static async loadStripeCustomerFromAccount(paymentConfig: any) {
    if (paymentConfig) {
      logger.info(
        `loadStripeCustomerFromAccount: ${paymentConfig.stripeCustomerId} stripeAccount: ${paymentConfig.stripeAccount}`
      )
      StorageManager.set(KEY_STRIPE_CUSTOMER, {
        id: paymentConfig.stripeCustomerId,
      })
      StorageManager.set(KEY_PAYMENT_METHOD_TYPE, PaymentMethodType.CREDIT_CARD)
      this.setStripeAccountName(paymentConfig.stripeAccount)
    }
  }

  static shouldLoadCardFromStripe() {
    const stripeCustomer = PaymentManager.getStripeCustomer()
    return (
      stripeCustomer?.id &&
      (!StorageManager.get(KEY_PAYMENT_CURRENT_CARD) ||
        !StorageManager.get(KEY_PAYMENT_METHOD_ID))
    )
  }

  static async loadCardFromStripe() {
    const stripeCustomer = PaymentManager.getStripeCustomer()!
    logger.info(`loadCardFromStripe stripeCustomerId: ${stripeCustomer.id}`)

    return mixtilesAxios
      .post('v3/stripe/listPaymentMethods', {
        customer: stripeCustomer.id,
        stripeAccountName: this.getStripeAccountName(),
      })
      .then((res) => {
        if (res.data.success.data.length === 0) {
          return
        }
        const paymentId = res.data.success.data[0].id
        const cc = res.data.success.data[0].card

        // save the last card added by the user to the local storage
        StorageManager.set(KEY_PAYMENT_CURRENT_CARD, cc)
        // save the payment method id to the local storage.
        // this will be required later to make payments
        StorageManager.set(KEY_PAYMENT_METHOD_ID, paymentId)
      })
      .catch((error) => {
        logger.error('Failed to load card from stripe', error)
      })
  }

  static getPaymentInfo() {
    return {
      ...fromPairs(
        ADDITIONAL_PAYMENT_INFO_KEYS.map((key) => [
          key,
          StorageManager.get(key),
        ])
      ),
      stripeCustomerId: (
        StorageManager.get<StripeCustomer>(KEY_STRIPE_CUSTOMER) || {}
      ).id,
    }
  }
}

export default PaymentManager
